// ═══ ModeBRow — one Mode B candidate row ════════════════════════════════
import React, { useState } from "react";
import { STRAT_COLORS } from "@/components/wheel/wheelConstants";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import {
  formatMoney, formatProbPct, formatStrikeLabel, formatDeltaToken,
  isValidProb, tradeBlockReason, tradeBlockLines, pairKey,
  isRefreshEligible, refreshOutcomeFor, inputQualityLabel,
  primaryRowStatus, rowStatusDetailLines, compactDeltaDisplay,
} from "@/lib/formatMarket";

const COL_COUNT = 19;

function TradeSelectControl({ e, tradeable, selected, onToggle, blockReason }) {
  const lines = tradeBlockLines(e);
  const tip = tradeable
    ? "Add this validated strategy to the trade basket"
    : (blockReason || "Cannot select for trade");
  return (
    <td className="text-center px-1 py-1.5 align-top" style={{ minWidth: 52 }}
      data-testid={`trade-cell-${e.ticker}`}>
      <label
        className="inline-flex flex-col items-center gap-0.5"
        title={tip}
        style={{ cursor: tradeable ? "pointer" : "help", pointerEvents: "auto" }}
        data-testid={`trade-label-${e.ticker}`}
      >
        <span className="text-[9px] font-medium leading-none" style={{ color: "var(--hoip-text-tertiary)" }}>Trade</span>
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-blue-600"
          checked={selected}
          aria-disabled={!tradeable}
          aria-label={tradeable ? `Select ${e.ticker} for trade` : `Cannot select ${e.ticker} for trade: ${blockReason || "blocked"}`}
          onChange={(ev) => {
            if (!tradeable) {
              ev.preventDefault();
              return;
            }
            onToggle(e);
          }}
          onClick={(ev) => {
            ev.stopPropagation();
            if (!tradeable) ev.preventDefault();
          }}
          onFocus={(ev) => {
            if (!tradeable && tip) ev.currentTarget.title = tip;
          }}
          style={tradeable ? undefined : { accentColor: "#71717A" }}
          data-testid={`check-${e.ticker}`}
        />
      </label>
      {!tradeable && lines.length > 0 ? (
        <span className="sr-only" data-testid={`trade-block-${e.ticker}`}>{lines.join(". ")}</span>
      ) : null}
    </td>
  );
}

function RefreshSelectControl({ e, refreshOk, refreshWhy, prepOn, onToggle }) {
  const tip = refreshOk
    ? (prepOn ? "Selected for data refresh, not added to trade basket." : "Select for refresh — prepare compact selected-expiry data (max 8). Independent of trade selection.")
    : refreshWhy;
  return (
    <td className="text-center px-1 py-1.5 align-top" style={{ minWidth: 52 }}
      data-testid={`refresh-cell-${e.ticker}`}>
      <label
        className="inline-flex flex-col items-center gap-0.5"
        title={tip}
        style={{
          cursor: refreshOk ? "pointer" : "not-allowed",
          pointerEvents: "auto",
          isolation: "isolate",
        }}
        data-testid={`refresh-label-${e.ticker}`}
      >
        <span className="text-[9px] font-medium leading-none" style={{ color: "var(--hoip-text-tertiary)" }}>Refresh</span>
        <input
          type="checkbox"
          className="h-3.5 w-3.5 accent-zinc-400"
          checked={prepOn}
          disabled={!refreshOk}
          aria-label={refreshOk
            ? `Select ${e.ticker} ${e.expiry || ""} for data refresh`
            : `Cannot select ${e.ticker} for refresh: ${refreshWhy}`}
          onChange={() => onToggle && onToggle(e)}
          onClick={(ev) => ev.stopPropagation()}
          data-testid={`prep-check-${e.ticker}`}
          data-wheel-refresh="1"
        />
      </label>
      {!refreshOk ? (
        <span className="sr-only" data-testid={`refresh-block-${e.ticker}`}>{refreshWhy}</span>
      ) : null}
    </td>
  );
}

export function ModeBRow({
  e, origIdx, fi, navigate,
  selected, toggle, entryKey,
  prepSelected, togglePrep,
  overrides, setOverrides,
  tooltipIdx, setTooltipIdx,
  onRefreshSnapshot, refreshBusy,
  preparation, prepareAck, lastRefreshReports,
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const needsSnapshotRefresh = e.selectable === false && (
    e.option_freshness?.status === "prior_session_stale"
    || e.option_freshness?.status === "stale"
    || e.option_freshness?.status === "timestamp_unverified"
    || String(e.freshness_label || "").toLowerCase().includes("refresh")
  );
  const refreshKey = `${e.ticker}|${e.expiry || ""}`;
  const rowRefreshing = refreshBusy === refreshKey;
  const _key = entryKey ? entryKey(e) : origIdx;
  const _pair = pairKey(e);
  const tradeable = e.selectable !== false && isValidProb(e.pop);
  const blockReason = tradeBlockReason(e);
  const refreshOk = isRefreshEligible(e);
  const refreshWhy = e.refresh_block_reason || "Unsupported or invalid symbol — cannot queue refresh.";
  const prepOn = Boolean(prepSelected && prepSelected.has(_pair));
  const jobOutcome = refreshOutcomeFor(e, preparation, prepareAck, lastRefreshReports);
  const qualityLabel = inputQualityLabel(e);
  const quoteLabel = e.pricing_display_label
    || (e.quote_complete === false ? (e.missing_quote_fields?.length ? "Partial quote data" : "Pricing unavailable") : null);
  const shownPrem = e.quote_complete && e.premium != null ? e.premium : e.indicative_premium;
  const shownYield = e.quote_complete && e.yield_pct != null ? e.yield_pct : e.indicative_yield_pct;
  const shownYpw = e.yield_pct_per_week;
  const primary = primaryRowStatus(e);
  const detailLines = rowStatusDetailLines(e, { jobOutcome });
  const detailTitle = detailLines.join("\n");
  return (
    <>
    <tr
      data-wheel-row="1"
      data-tradeable={tradeable ? "1" : "0"}
      data-refresh-eligible={refreshOk ? "1" : "0"}
      className={`border-b transition-colors relative ${selected.has(_key) ? "bg-blue-600/5" : "hover:bg-[var(--hoip-hover)]"}`}
      style={{ borderColor: "var(--hoip-border)", opacity: 1, color: "var(--hoip-text-primary)" }}
      data-testid={`mode-b-row-${e.ticker}`}>
      <TradeSelectControl
        e={e}
        tradeable={tradeable}
        selected={selected.has(_key)}
        onToggle={toggle}
        blockReason={blockReason}
      />
      <RefreshSelectControl
        e={e}
        refreshOk={refreshOk}
        refreshWhy={refreshWhy}
        prepOn={prepOn}
        onToggle={togglePrep}
      />
      <td className="font-mono-data text-xs font-medium px-1 text-blue-400 cursor-pointer hover:underline"
        onClick={() => navigate(`/ticker/${e.ticker}`)}>{e.ticker}</td>
      <td className="text-[10px] truncate max-w-[60px]" style={{ color: "var(--hoip-text-secondary)" }}>{e.sector}</td>
      <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-primary)" }}>
        <span title={e.price_guardrail_reason || undefined}>{formatMoney(e.price)}</span>
        {e.price_guardrail_flag && (
          <span
            className="ml-1 text-[8px] align-middle"
            style={{ color: e.price_guardrail_flag?.startsWith("rejected") ? "#F87171" : "var(--hoip-text-tertiary)" }}
            title={e.price_guardrail_reason || e.price_guardrail_flag}
            data-testid={`price-flag-${e.ticker}`}
          >
            {e.price_guardrail_flag?.startsWith("rejected") ? "!" : "*"}
          </span>
        )}
        {e.liquidity?.is_thin && (
          <span
            className="ml-1 text-[8px] align-middle"
            style={{ color: "var(--hoip-text-tertiary)" }}
            title={`Thin liquidity: ${e.liquidity.thin_reason}`}
            data-testid={`liquidity-flag-${e.ticker}`}
          >
            ◐
          </span>
        )}
      </td>
      <td className="text-right font-mono-data text-[10px]"
        title={e.iv_rank == null ? "IV Rank not ready — need 60+ valid daily ATM IV observations"
          : `Percentile rank of current ATM IV in a 252-day valid-IV window. ${e.iv_rank < 35 ? "Low IV" : e.iv_rank > 60 ? "High IV" : "Moderate IV"}`}>
        <span className={e.iv_rank == null ? "" : e.iv_rank < 35 ? "text-blue-400" : e.iv_rank > 60 ? "text-red-400" : ""}
          style={e.iv_rank == null ? { color: "var(--hoip-text-secondary)" } : undefined}>
          {e.iv_rank == null ? (e.iv_rank_quality === "preparing" ? "preparing" : "—") : `${e.iv_rank}`}
        </span>
      </td>
      <td className="text-right font-mono-data text-[10px] font-bold text-emerald-500">{e.wheel_score}</td>
      <td className="text-center relative">
        <div className="inline-flex items-center gap-0.5">
          <button
            className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold border cursor-pointer ${STRAT_COLORS[e.strategy] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}`}
            onMouseEnter={() => setTooltipIdx(origIdx)}
            onMouseLeave={() => setTooltipIdx(null)}
            data-testid={`strat-badge-${e.ticker}`}>{e.strategy}{e.overridden ? " \u270E" : ""}</button>
          <select value={overrides[origIdx] || ""} onChange={ev => {
              const v = ev.target.value;
              setOverrides(prev => ({ ...prev, [origIdx]: v || undefined }));
            }}
            className="w-5 h-5 opacity-0 absolute cursor-pointer" style={{ right: 0 }}
            data-testid={`strat-override-${e.ticker}`}>
            <option value="">Auto</option>
            <option value="CSP">CSP</option>
            <option value="BPS">BPS</option>
            <option value="DCS">DCS</option>
          </select>
        </div>
        {tooltipIdx === origIdx && e.comparison && (
          <div className="absolute z-50 left-1/2 -translate-x-1/2 top-full mt-1 w-72 border rounded-sm p-2 shadow-xl text-left"
            style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
            data-testid={`tooltip-${e.ticker}`}>
            <div className="text-[9px] font-bold mb-1" style={{ color: "var(--hoip-text-secondary)" }}>Strategy: {e.strategy}</div>
            <div className="text-[8px] mb-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>{e.strategy_reason}</div>
            <table className="w-full text-[8px] mb-1.5">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--hoip-divider)" }}>
                  <th className="text-left py-0.5" style={{ color: "var(--hoip-text-tertiary)" }}></th>
                  <th className="text-right" style={{ color: "var(--hoip-text-tertiary)" }}>Gain</th>
                  <th className="text-right" style={{ color: "var(--hoip-text-tertiary)" }}>Loss</th>
                  <th className="text-left pl-1" style={{ color: "var(--hoip-text-tertiary)" }}>B/E</th>
                </tr>
              </thead>
              <tbody>
                {["DCS", "BPS", "CSP"].map(s => {
                  const alt = e.comparison[s];
                  if (!alt) return null;
                  const isCurrent = s === e.strategy;
                  return (
                    <tr key={s} className={isCurrent ? "font-bold" : ""} style={{ color: isCurrent ? "var(--hoip-text-primary)" : "var(--hoip-text-secondary)" }}>
                      <td className="py-0.5"><span className={`px-1 rounded-sm ${STRAT_COLORS[s]?.split(" ").slice(0, 2).join(" ")}`}>{s}</span>{isCurrent ? " *" : ""}</td>
                      <td className="text-right font-mono-data text-emerald-500">${alt.max_gain}</td>
                      <td className="text-right font-mono-data text-red-400">-${Math.abs(alt.max_loss)}</td>
                      <td className="text-left pl-1 font-mono-data">{alt.breakeven}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[8px] pt-1 border-t" style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}>
              {e.comparison[e.strategy]?.note}
            </div>
          </div>
        )}
      </td>
      <td className="text-left font-mono-data text-[10px]" style={{ color: "var(--hoip-text-primary)" }}>
        <div data-testid={`strikes-${e.ticker}`}>{formatStrikeLabel(e)}</div>
        <div className="text-[9px]" style={{ color: "var(--hoip-text-secondary)" }}>
          {e.option_type ? `${e.option_type} ` : ""}{e.expiry ? `Exp ${e.expiry}` : ""}
          {e.dte != null ? ` · ${e.dte}d` : ""}
        </div>
        {primary ? (
          <div className="mt-0.5 flex items-center gap-1 flex-wrap">
            <StatusBadge
              tier={primary.tier}
              text={primary.text}
              title={detailTitle || primary.full}
              expanded={detailOpen}
              onClick={() => setDetailOpen((v) => !v)}
              testid={`freshness-${e.ticker}`}
            />
            {needsSnapshotRefresh && onRefreshSnapshot ? (
              <button
                type="button"
                className="text-[9px] underline"
                style={{ color: "var(--hoip-text-tertiary)" }}
                disabled={rowRefreshing}
                onClick={(ev) => { ev.stopPropagation(); onRefreshSnapshot(e); }}
                title="Explicit ticker+expiry snapshot refresh. Bounded vendor call for this row only."
                data-testid={`refresh-snapshot-${e.ticker}`}
              >
                {rowRefreshing ? "Refreshing…" : "Refresh"}
              </button>
            ) : null}
          </div>
        ) : null}
        {prepOn ? (
          <span className="sr-only" data-testid={`prep-note-${e.ticker}`}>
            Selected for data refresh, not added to trade basket.
          </span>
        ) : null}
        {jobOutcome ? (
          <span className="sr-only" data-testid={`refresh-outcome-${e.ticker}`}>{jobOutcome}</span>
        ) : null}
      </td>
      <td className="text-center font-mono-data text-[10px]">
        <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold border bg-blue-500/10 text-blue-400 border-blue-500/20"
          title={e.sizing_reason || (e.max_contracts_for_size ? `Sizing max: ${e.max_contracts_for_size}` : "")}
          data-testid={`contracts-${e.ticker}`}>
          {e.contracts || 0}x
          {e.capped && (
            <span className="ml-1 text-[8px] font-normal" style={{ color: "var(--hoip-text-tertiary)" }}
              data-testid={`sizing-capped-${e.ticker}`}>
              capped
            </span>
          )}
        </span>
      </td>
      <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-primary)" }}>${e.capital_req?.toLocaleString()}</td>
      <td className="text-right font-mono-data text-[10px] text-emerald-400"
        title={quoteLabel || e.pricing_detail || undefined}
        data-testid={`premium-${e.ticker}`}>
        {shownPrem != null ? `$${(Number(shownPrem) * 100).toFixed(0)}` : "—"}
      </td>
      <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-primary)" }}>
        {e.quote_complete && e.yield_pct != null
          ? `${e.yield_pct}%`
          : shownYield != null
            ? `${shownYield}%`
            : "—"}
      </td>
      <td className="text-right font-mono-data text-[10px]"
        style={{ color: "var(--hoip-text-primary)" }}
        title={`${shownYpw ?? "—"}% per week (${e.below_yield_floor ? "BELOW floor" : "above floor"})`}
        data-testid={`mode-b-yield-pw-${e.ticker}`}>
        {shownYpw != null ? `${shownYpw}%` : "—"}
      </td>
      <td className="text-right font-mono-data text-[10px]"
        style={{ color: "var(--hoip-text-primary)" }}
        data-testid={`mode-b-delta-${e.ticker}`}
        title={
          e.delta_quality === "stale" || e.delta_source === "stale" || e.delta_freshness === "stale"
            || e.option_freshness?.status === "prior_session_stale" || e.option_freshness?.status === "stale"
            ? (e.option_freshness?.banner || "Provider Δ is stale — refresh required before using current target-delta selection")
            : formatDeltaToken(
              e.short_delta ?? e.delta,
              e.delta_source || e.short_delta_source,
              e.delta_label,
              { freshness: e.delta_freshness || e.short_delta_freshness || e.option_freshness?.status, delay_class: e.delay_class, as_of: e.as_of },
            )
        }>
        {compactDeltaDisplay(e)}
      </td>
      <td className="text-right font-mono-data text-[10px]"
        style={{ color: "var(--hoip-text-primary)" }}
        data-testid={`mode-b-pop-${e.ticker}`}
        title={qualityLabel ? `Probability of Profit — 1 − |Δ| · ${qualityLabel}` : "Probability of Profit — 1 − |Δ|"}>
        {formatProbPct(e.pop)}
        {qualityLabel ? (
          <span className="sr-only" data-testid={`pop-quality-${e.ticker}`}>{qualityLabel}</span>
        ) : null}
      </td>
      <td className="text-right font-mono-data text-[10px]"
        style={{ color: "var(--hoip-text-primary)" }}
        data-testid={`mode-b-pot-${e.ticker}`}
        title={qualityLabel ? `Probability of Touch = 1 − P(non-touch) · ${qualityLabel}` : "Probability of Touch = 1 − P(non-touch)"}>
        {formatProbPct(e.pot)}
      </td>
      <td className="text-center">
        {e.regime_fit ? (
          <span className={`text-[9px] px-1 py-0.5 rounded-sm font-bold ${
            e.regime_fit === "aligns" ? "bg-emerald-500/10 text-emerald-400" :
            e.regime_fit === "against" ? "bg-red-500/10 text-red-400" :
            "text-zinc-400"
          }`} data-testid={`mode-b-regime-fit-${e.ticker}`}>
            {e.regime_fit}
          </span>
        ) : (
          <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</span>
        )}
      </td>
      <td className="text-[10px] truncate max-w-[80px]" style={{ color: "var(--hoip-text-secondary)" }}>{e.regime}</td>
    </tr>
    {detailOpen && detailLines.length > 0 ? (
      <tr data-testid={`row-detail-${e.ticker}`} style={{ background: "var(--hoip-bg)" }}>
        <td colSpan={COL_COUNT} className="px-3 py-2 text-[10px] leading-relaxed"
          style={{ color: "var(--hoip-text-secondary)", borderColor: "var(--hoip-border)" }}>
          {detailLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </td>
      </tr>
    ) : null}
    </>
  );
}
